(() => {
  const root = document.documentElement;
  const viewport = window.visualViewport;
  const mobile = window.matchMedia('(max-width: 1099px), (pointer: coarse)');
  let frame = null;
  let savedScroll = null;
  let restoreFrame = null;
  let headerPlaceholder = null;
  const observed = new WeakSet();

  const update = () => {
    frame = null;
    const unzoomed = viewport && Math.abs(viewport.scale - 1) < 0.01;
    const height = unzoomed ? viewport.height : window.innerHeight;
    const top = unzoomed ? Math.max(0, viewport.offsetTop) : 0;
    if (height > 0) {
      root.style.setProperty('--ts-overlay-height', `${height}px`);
      root.style.setProperty('--ts-overlay-top', `${top}px`);
    }
  };

  const scheduleUpdate = () => {
    if (frame === null) frame = window.requestAnimationFrame(update);
  };

  const getScrollPosition = () => savedScroll || { x: window.scrollX, y: window.scrollY };

  const hasOpenPanel = () => !!(
    document.body.classList.contains('overflow-hidden') ||
    root.classList.contains('ts-wishlist-open') ||
    document.querySelector('header-drawer > details[open], .mobile-facets__disclosure[open], .ts-wishlist-panel:not([hidden])')
  );

  const isScrollLocked = () => !!savedScroll || restoreFrame !== null || hasOpenPanel();

  const syncLock = () => {
    const shouldLock = mobile.matches && hasOpenPanel();

    if (shouldLock && !savedScroll) {
      window.cancelAnimationFrame(restoreFrame);
      restoreFrame = null;
      savedScroll = { x: window.scrollX, y: window.scrollY };
      const header = document.querySelector('.section-header');
      if (header) {
        // Keep both its viewport position and its space in the page while the body is fixed.
        root.style.setProperty('--ts-overlay-header-top', `${header.getBoundingClientRect().top}px`);
        headerPlaceholder = document.createElement('div');
        headerPlaceholder.setAttribute('aria-hidden', 'true');
        headerPlaceholder.style.setProperty('display', 'block', 'important');
        headerPlaceholder.style.height = `${header.offsetHeight}px`;
        header.before(headerPlaceholder);
      }
      root.classList.add('ts-header-scroll-paused');
      root.style.setProperty('--ts-overlay-scroll-top', `${-savedScroll.y}px`);
      root.classList.add('ts-overlay-locked');
    } else if (!shouldLock && savedScroll) {
      const position = savedScroll;
      savedScroll = null;
      root.classList.remove('ts-overlay-locked');
      root.style.removeProperty('--ts-overlay-scroll-top');
      root.style.removeProperty('--ts-overlay-header-top');
      headerPlaceholder?.remove();
      headerPlaceholder = null;
      window.scrollTo({ left: position.x, top: position.y, behavior: 'instant' });
      const stickyHeader = document.querySelector('sticky-header');
      if (stickyHeader) stickyHeader.currentScrollTop = position.y;
      // Ignore the synthetic scroll events produced by releasing the fixed body.
      restoreFrame = window.requestAnimationFrame(() => {
        if (stickyHeader) stickyHeader.currentScrollTop = window.scrollY;
        restoreFrame = window.requestAnimationFrame(() => {
          if (stickyHeader) stickyHeader.currentScrollTop = window.scrollY;
          restoreFrame = null;
          root.classList.remove('ts-header-scroll-paused');
        });
      });
    }
    update();
  };

  const observer = new MutationObserver(syncLock);
  const observePanels = () => {
    [root, document.body, ...document.querySelectorAll('header-drawer > details, .mobile-facets__disclosure, .ts-wishlist-panel')].forEach((element) => {
      if (observed.has(element)) return;
      observed.add(element);
      observer.observe(element, { attributes: true, attributeFilter: ['class', 'open', 'hidden'] });
    });
    syncLock();
  };

  window.tsOverlayViewport = { update, getScrollPosition, isScrollLocked };
  window.addEventListener('resize', scheduleUpdate, { passive: true });
  window.addEventListener('orientationchange', scheduleUpdate, { passive: true });
  window.addEventListener('pageshow', observePanels);
  viewport?.addEventListener('resize', scheduleUpdate, { passive: true });
  viewport?.addEventListener('scroll', scheduleUpdate, { passive: true });
  mobile.addEventListener('change', syncLock);
  document.addEventListener('shopify:section:load', observePanels);
  observePanels();
})();

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus({ preventScroll: true });

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus({ preventScroll: true });
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.closeAnimationFrames = new Map();
    this.openAnimationFrame = null;

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) {
        event.preventDefault();

        if (detailsElement.classList.contains('is-closing')) {
          this.openMenuDrawer(summaryElement);
        } else {
          this.closeMenuDrawer(event, summaryElement);
        }
      } else {
        this.openMenuDrawer(summaryElement);
      }

      if (window.matchMedia('(max-width: 990px)').matches) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    this.cancelCloseAnimation(this.mainDetailsToggle);
    this.mainDetailsToggle.classList.remove('is-closing');
    this.scheduleOpenAnimation(this.mainDetailsToggle);
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;
    if (this.mainDetailsToggle.classList.contains('is-closing')) return;

    this.cancelOpenAnimation();
    this.mainDetailsToggle.classList.add('is-closing');
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    if (detailsElement === this.mainDetailsToggle) {
      event.preventDefault();
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
      return;
    }

    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    this.cancelCloseAnimation(detailsElement);

    let animationStart;
    const drawer = detailsElement.querySelector(':scope > .menu-drawer');
    const drawerDuration = drawer
      ? parseFloat(window.getComputedStyle(drawer).getPropertyValue('--ts-drawer-motion-duration'))
      : NaN;
    const animationDuration = detailsElement.matches('.mobile-facets__disclosure')
      ? 560
      : detailsElement.id === 'Details-menu-drawer-container'
        ? Number.isFinite(drawerDuration) ? drawerDuration + (drawerDuration > 0 ? 34 : 0) : 520
        : 360;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < animationDuration) {
        this.closeAnimationFrames.set(detailsElement, window.requestAnimationFrame(handleAnimation));
      } else {
        this.closeAnimationFrames.delete(detailsElement);
        detailsElement.removeAttribute('open');
        detailsElement.classList.remove('is-closing');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    this.closeAnimationFrames.set(detailsElement, window.requestAnimationFrame(handleAnimation));
  }

  cancelCloseAnimation(detailsElement) {
    const frame = this.closeAnimationFrames.get(detailsElement);
    if (frame !== undefined) {
      window.cancelAnimationFrame(frame);
      this.closeAnimationFrames.delete(detailsElement);
    }
  }

  scheduleOpenAnimation(detailsElement) {
    this.cancelOpenAnimation();

    this.openAnimationFrame = window.requestAnimationFrame(() => {
      this.openAnimationFrame = window.requestAnimationFrame(() => {
        this.openAnimationFrame = null;
        if (!detailsElement.hasAttribute('open')) return;
        detailsElement.classList.add('menu-opening');
      });
    });
  }

  cancelOpenAnimation() {
    if (this.openAnimationFrame === null) return;
    window.cancelAnimationFrame(this.openAnimationFrame);
    this.openAnimationFrame = null;
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.mainDetailsToggle.addEventListener('toggle', () => {
      if (!this.mainDetailsToggle.open) {
        this.drawerMotion?.cancel();
        this.drawerMotion = null;
        this.querySelector('#menu-drawer')?.style.removeProperty('will-change');
        this.mainDetailsToggle.classList.remove('menu-opening', 'is-closing', 'is-preparing-open');
      }
    });
  }

  onSummaryClick(event) {
    if (event.currentTarget.parentNode !== this.mainDetailsToggle) { super.onSummaryClick(event); return; }
    event.preventDefault();
    if (this.mainDetailsToggle.open && !this.mainDetailsToggle.classList.contains('is-closing')) this.closeMenuDrawer(event, event.currentTarget);
    else this.openMenuDrawer(event.currentTarget);
  }

  animateDrawer(opening, finish, fromClosed = false) {
    const details = this.mainDetailsToggle;
    const drawer = details.querySelector(':scope > .menu-drawer');
    const from = fromClosed ? 'translate3d(-100%,0,0)' : getComputedStyle(drawer).transform;
    const fromOpacity = fromClosed ? 0 : Number(getComputedStyle(drawer).opacity);
    this.drawerMotion?.cancel();
    this.drawerMotion = null;
    details.classList.toggle('menu-opening', opening);
    details.classList.toggle('is-closing', !opening);
    details.classList.remove('is-preparing-open');
    const to = opening ? 'translate3d(0,0,0)' : 'translate3d(-100%,0,0)';
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !drawer.animate) { finish?.(); return; }
    drawer.style.willChange = 'transform';
    const motion = drawer.animate([{ transform: from, opacity: fromOpacity }, { transform: to, opacity: opening ? 1 : 0 }], {
      duration: 350,
      easing: opening ? 'cubic-bezier(.75,.23,.57,.65)' : 'cubic-bezier(.43,.35,.25,.77)',
      fill: 'both',
    });
    this.drawerMotion = motion;
    motion.finished.then(() => {
      if (this.drawerMotion !== motion) return;
      this.drawerMotion = null;
      finish?.();
      motion.cancel();
      drawer.style.removeProperty('will-change');
    }, () => {});
  }

  openMenuDrawer(summaryElement) {
    const fromClosed = !this.mainDetailsToggle.open;
    this.cancelCloseAnimation(this.mainDetailsToggle);
    this.cancelOpenAnimation();
    this.mainDetailsToggle.open = true;
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset = this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    this.onResize();
    this.header?.classList.add('menu-open');
    document.documentElement.classList.add('ts-drawer-open');
    document.body.classList.add('overflow-hidden-' + this.dataset.breakpoint);
    summaryElement.setAttribute('aria-expanded', 'true');
    const drawer = this.querySelector('#menu-drawer');
    drawer.inert = false;
    this.animateDrawer(true, () => {
      if (this.mainDetailsToggle.open && this.mainDetailsToggle.classList.contains('menu-opening')) trapFocus(drawer, drawer.querySelector('[data-ts-drawer-close]') || drawer);
    }, fromClosed);
    window.addEventListener('resize', this.onResize);
  }

  closeMenuDrawer(event, elementToFocus = this.mainDetailsToggle.querySelector(':scope > summary')) {
    if (event === undefined || !this.mainDetailsToggle.open || this.mainDetailsToggle.classList.contains('is-closing')) return;
    this.cancelOpenAnimation();
    this.cancelCloseAnimation(this.mainDetailsToggle);
    elementToFocus?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(elementToFocus);
    this.querySelector('#menu-drawer').inert = true;
    this.animateDrawer(false, () => {
      this.mainDetailsToggle.open = false;
      this.mainDetailsToggle.classList.remove('is-closing', 'menu-opening');
      this.mainDetailsToggle.querySelectorAll('details').forEach(details => { this.cancelCloseAnimation(details); details.open = false; details.classList.remove('menu-opening'); details.querySelector('summary')?.setAttribute('aria-expanded', 'false'); });
      this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(menu => menu.classList.remove('submenu-open'));
      this.header?.classList.remove('menu-open');
      document.documentElement.classList.remove('ts-drawer-open');
      document.body.classList.remove('overflow-hidden-' + this.dataset.breakpoint);
      window.removeEventListener('resize', this.onResize);
    });
  }

  onResize = () => {
    if (this.header) document.documentElement.style.setProperty('--header-bottom-position', Math.round(this.header.getBoundingClientRect().bottom - this.borderOffset) + 'px');
    document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px');
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

(() => {
  const storageKey = 'timeshop:wishlist:v1';
  const maxCompareItems = 4;
  let wishlist = [];
  const compareSelection = new Set();
  let compareRequestId = 0;
  let closeTimer = null;
  let refreshFrame = null;
  let openFrame = null;
  let openTimer = null;
  const technicalSpecsCache = new Map();
  let toastTimer = null;
  let toastHideTimer = null;

  const readWishlist = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      wishlist = Array.isArray(parsed) ? parsed.filter((item) => item && item.id).slice(0, 120) : [];
    } catch (error) {
      wishlist = [];
    }

    return wishlist;
  };

  const writeWishlist = () => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(wishlist));
    } catch (error) {
      return;
    }
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    })[char]);

  const safeStoreUrl = (value, fallback = '#') => {
    try {
      const rawValue = String(value || '').trim();
      if (!rawValue) return fallback;
      const url = new URL(rawValue, window.location.origin);
      return url.origin === window.location.origin ? url.href : fallback;
    } catch (error) {
      return fallback;
    }
  };

  const safeWishlistImageUrl = (value, fallback = '') => {
    try {
      const rawValue = String(value || '').trim();
      if (!rawValue) return fallback;
      const url = new URL(rawValue, window.location.origin);
      const isShopifyImageHost =
        url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com');
      const isAllowedProtocol = url.protocol === 'https:' || url.protocol === 'http:';

      return isAllowedProtocol && (url.origin === window.location.origin || isShopifyImageHost)
        ? url.href
        : fallback;
    } catch (error) {
      return fallback;
    }
  };

  const formatMoney = (value) => {
    const amount = (Number(value) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'bg-BG', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  const getItemFromButton = (button) => {
    const id = button.dataset.tsWishlistId;
    const title = button.dataset.tsWishlistTitle;
    if (!id || !title) return null;

    return {
      id,
      handle: button.dataset.tsWishlistHandle || '',
      title,
      vendor: button.dataset.tsWishlistVendor || '',
      url: button.dataset.tsWishlistUrl || '#',
      image: button.dataset.tsWishlistImage || '',
      price: button.dataset.tsWishlistPrice || '',
      comparePrice: button.dataset.tsWishlistComparePrice || '',
      available: button.dataset.tsWishlistAvailable === 'true',
      type: button.dataset.tsWishlistType || '',
      sku: button.dataset.tsWishlistSku || '',
    };
  };

  const isFavorite = (id) => wishlist.some((item) => item.id === id);

  const updateButtons = () => {
    document.querySelectorAll('[data-ts-wishlist-toggle]').forEach((button) => {
      const active = isFavorite(button.dataset.tsWishlistId);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? 'Премахни от любими' : 'Добави в любими');
    });
  };

  const updateHeader = () => {
    const count = wishlist.length;

    document.querySelectorAll('[data-ts-wishlist-trigger]').forEach((trigger) => {
      trigger.classList.toggle('is-active', count > 0);
      trigger.setAttribute('aria-label', count > 0 ? `Любими (${count})` : 'Любими');
    });

    document.querySelectorAll('[data-ts-wishlist-count]').forEach((bubble) => {
      bubble.hidden = count === 0;
      bubble.textContent = count > 99 ? '99+' : String(count);
    });

    document.querySelectorAll('[data-ts-wishlist-inline-count]').forEach((counter) => {
      counter.textContent = String(count);
    });
  };

  const getWishlistToast = () => {
    let toast = document.querySelector('[data-ts-wishlist-toast]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'ts-wishlist-toast';
    toast.dataset.tsWishlistToast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');

    const icon = document.createElement('span');
    icon.className = 'ts-wishlist-toast__icon';
    icon.setAttribute('aria-hidden', 'true');

    const copy = document.createElement('span');
    copy.className = 'ts-wishlist-toast__copy';

    const title = document.createElement('strong');
    title.className = 'ts-wishlist-toast__title';

    const product = document.createElement('span');
    product.className = 'ts-wishlist-toast__product';

    copy.append(title, product);
    toast.append(icon, copy);
    document.body.appendChild(toast);
    return toast;
  };

  const showWishlistToast = (item, added) => {
    if (!item) return;

    const toast = getWishlistToast();
    const heading = toast.querySelector('.ts-wishlist-toast__title');
    const product = toast.querySelector('.ts-wishlist-toast__product');

    heading.textContent = added ? 'Добавено в любими' : 'Премахнато от любими';
    product.textContent = item.title;
    toast.classList.remove('is-visible', 'is-added', 'is-removed');
    void toast.offsetWidth;
    toast.classList.add(added ? 'is-added' : 'is-removed', 'is-visible');

    window.clearTimeout(toastTimer);
    window.clearTimeout(toastHideTimer);
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, added ? 2800 : 2200);
    toastHideTimer = window.setTimeout(() => {
      toast.classList.remove('is-added', 'is-removed');
    }, added ? 3300 : 2700);
  };

  const playWishlistAnimation = (button, added) => {
    button.classList.remove('is-popping', 'is-adding', 'is-removing');
    void button.offsetWidth;
    button.classList.add('is-popping', added ? 'is-adding' : 'is-removing');

    window.clearTimeout(button.tsWishlistAnimationTimer);
    button.tsWishlistAnimationTimer = window.setTimeout(() => {
      button.classList.remove('is-popping', 'is-adding', 'is-removing');
    }, 900);

    if (!added || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const rect = button.getBoundingClientRect();
    const burst = document.createElement('span');
    burst.className = 'ts-wishlist-burst';
    burst.setAttribute('aria-hidden', 'true');
    burst.style.left = `${rect.left + rect.width / 2}px`;
    burst.style.top = `${rect.top + rect.height / 2}px`;

    const colors = ['#e12626', '#ff5b69', '#334fb4', '#ff9f1c'];
    const particleCount = 10;
    for (let index = 0; index < particleCount; index += 1) {
      const angle = (Math.PI * 2 * index) / particleCount - Math.PI / 2;
      const distance = 32 + (index % 3) * 7;
      const particle = document.createElement('span');
      particle.className = 'ts-wishlist-burst__particle';
      particle.style.setProperty('--ts-burst-x', `${Math.cos(angle) * distance}px`);
      particle.style.setProperty('--ts-burst-y', `${Math.sin(angle) * distance}px`);
      particle.style.setProperty('--ts-burst-delay', `${(index % 2) * 24}ms`);
      particle.style.setProperty('--ts-burst-color', colors[index % colors.length]);
      burst.appendChild(particle);
    }

    document.body.appendChild(burst);
    window.setTimeout(() => burst.remove(), 900);
  };

  const renderWishlist = () => {
    const list = document.querySelector('[data-ts-wishlist-list]');
    if (!list) return;

    if (!wishlist.length) {
      compareSelection.clear();
      const root = window.Shopify?.routes?.root || '/';
      const collectionUrl = safeStoreUrl(`${root.endsWith('/') ? root : `${root}/`}collections/ново`);
      list.innerHTML = `
        <div class="ts-wishlist-empty">
          <span class="ts-wishlist-empty__icon" aria-hidden="true">♡</span>
          <h3>Все още нямаш любими</h3>
          <p>Запази моделите, които харесваш, и ги сравни по-късно.</p>
          <a class="ts-wishlist-empty__link" href="${escapeHtml(collectionUrl)}">Разгледай новите модели</a>
        </div>
      `;
      updateCompareControls();
      return;
    }

    list.innerHTML = wishlist.map((item) => {
      const title = escapeHtml(item.title);
      const vendor = escapeHtml(item.vendor);
      const price = escapeHtml(item.price);
      const comparePrice = escapeHtml(item.comparePrice || '');
      const url = escapeHtml(safeStoreUrl(item.url));
      const safeImage = safeWishlistImageUrl(item.image, '');
      const image = escapeHtml(safeImage);
      const selected = compareSelection.has(String(item.id));
      const imageMarkup = image
        ? `<img src="${image}" alt="${title}" loading="lazy">`
        : '<span aria-hidden="true">♡</span>';

      return `
        <article class="ts-wishlist-item${selected ? ' is-selected' : ''}" data-ts-wishlist-item-id="${escapeHtml(item.id)}">
          <a class="ts-wishlist-item__image" href="${url}" aria-label="${title}">
            ${imageMarkup}
          </a>
          <a class="ts-wishlist-item__meta" href="${url}">
            ${vendor ? `<div class="ts-wishlist-item__vendor">${vendor}</div>` : ''}
            <div class="ts-wishlist-item__title">${title}</div>
            ${price ? `<div class="ts-wishlist-item__price">${comparePrice ? `<s>${comparePrice}</s>` : ''}<strong>${price}</strong></div>` : ''}
          </a>
          <div class="ts-wishlist-item__actions">
            <button
              class="ts-wishlist-item__compare${selected ? ' is-selected' : ''}"
              type="button"
              aria-label="${selected ? 'Премахни от сравнението' : 'Добави за сравнение'}"
              aria-pressed="${selected ? 'true' : 'false'}"
              data-ts-wishlist-compare-toggle="${escapeHtml(item.id)}"
            >
              <span aria-hidden="true">✓</span>
              <em>Сравни</em>
            </button>
            <button class="ts-wishlist-item__remove" type="button" aria-label="Премахни от любими" data-ts-wishlist-remove="${escapeHtml(item.id)}">
              ×
            </button>
          </div>
        </article>
      `;
    }).join('');

    updateCompareControls();
  };

  const pruneCompareSelection = () => {
    const wishlistIds = new Set(wishlist.map((item) => String(item.id)));
    Array.from(compareSelection).forEach((id) => {
      if (!wishlistIds.has(id)) compareSelection.delete(id);
    });
  };

  const updateCompareControls = (statusMessage = '') => {
    pruneCompareSelection();
    const count = compareSelection.size;
    const compareView = document.querySelector('[data-ts-wishlist-compare-view]');
    const comparisonIsOpen = compareView && !compareView.hidden;
    const selectionBar = document.querySelector('[data-ts-wishlist-selection-bar]');
    const footer = document.querySelector('[data-ts-wishlist-footer]');
    const compareButton = document.querySelector('[data-ts-wishlist-compare]');
    const selectionStatus = document.querySelector('[data-ts-wishlist-selection-status]');
    const compareCount = document.querySelector('[data-ts-wishlist-compare-count]');

    if (selectionBar) selectionBar.hidden = wishlist.length === 0;
    if (footer) footer.hidden = wishlist.length === 0 || comparisonIsOpen;
    if (compareButton) compareButton.disabled = count < 2;
    if (compareCount) compareCount.textContent = String(count);
    if (selectionStatus) {
      selectionStatus.textContent = statusMessage || `${count} ${count === 1 ? 'избран' : 'избрани'}`;
    }

    document.querySelectorAll('[data-ts-wishlist-compare-toggle]').forEach((button) => {
      const selected = compareSelection.has(String(button.dataset.tsWishlistCompareToggle));
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.setAttribute('aria-label', selected ? 'Премахни от сравнението' : 'Добави за сравнение');
      button.closest('.ts-wishlist-item')?.classList.toggle('is-selected', selected);
    });
  };

  const showWishlistList = () => {
    compareRequestId += 1;
    const listView = document.querySelector('[data-ts-wishlist-list-view]');
    const compareView = document.querySelector('[data-ts-wishlist-compare-view]');
    const panel = document.querySelector('[data-ts-wishlist-panel]');

    if (listView) listView.hidden = false;
    if (compareView) compareView.hidden = true;
    panel?.classList.remove('is-comparing');
    updateCompareControls();
  };

  const normalizeSpecificationText = (value = '', maxLength = 300) =>
    String(value).replace(/\s+/g, ' ').trim().slice(0, maxLength);

  const getTechnicalSpecifications = async (item, handle, normalizedRoot) => {
    const cacheKey = String(handle || item.id || item.url || '');
    if (technicalSpecsCache.has(cacheKey)) return technicalSpecsCache.get(cacheKey);

    const handleUrl = handle
      ? safeStoreUrl(`${normalizedRoot}products/${encodeURIComponent(handle)}`, '')
      : '';
    const productPageUrl = safeStoreUrl(item.url, '') || handleUrl;
    if (!productPageUrl) return [];

    try {
      const response = await fetch(productPageUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return [];

      const markup = await response.text();
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const specifications = [];
      const seenLabels = new Set();

      documentFragment.querySelectorAll('.ts-tech-specs__body .ts-tech-specs__row').forEach((row) => {
        if (specifications.length >= 30) return;

        const label = normalizeSpecificationText(
          row.querySelector('.ts-tech-specs__label')?.textContent || '',
          100
        );
        const value = normalizeSpecificationText(
          row.querySelector('.ts-tech-specs__value')?.textContent || ''
        );
        const normalizedLabel = label.toLocaleLowerCase('bg-BG');
        if (!label || !value || seenLabels.has(normalizedLabel)) return;

        seenLabels.add(normalizedLabel);
        specifications.push({ label, value });
      });

      technicalSpecsCache.set(cacheKey, specifications);
      return specifications;
    } catch (error) {
      return [];
    }
  };

  const getWishlistProductDetails = async (item) => {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    let handle = item.handle || '';

    if (!handle && item.url) {
      try {
        const parts = new URL(item.url, window.location.origin).pathname.split('/products/');
        handle = decodeURIComponent((parts[1] || '').split(/[/?#]/)[0]);
      } catch (error) {
        handle = '';
      }
    }

    const technicalSpecsPromise = getTechnicalSpecifications(item, handle, normalizedRoot);

    let product = null;
    if (handle) {
      try {
        const response = await fetch(`${normalizedRoot}products/${encodeURIComponent(handle)}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (response.ok) product = await response.json();
      } catch (error) {
        product = null;
      }
    }

    const technicalSpecs = await technicalSpecsPromise;
    const getSpecificationValue = (...labels) => {
      const normalizedLabels = labels.map((label) => label.toLocaleLowerCase('bg-BG'));
      return technicalSpecs.find((specification) =>
        normalizedLabels.includes(specification.label.toLocaleLowerCase('bg-BG'))
      )?.value || '';
    };

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant = variants.find((candidate) => candidate.available) || variants[0] || null;
    const productImage = typeof product?.featured_image === 'string'
      ? product.featured_image
      : product?.featured_image?.src || product?.featured_image?.url || '';

    return {
      id: String(item.id),
      title: product?.title || item.title || '',
      vendor: product?.vendor || item.vendor || getSpecificationValue('Производител', 'Марка') || '—',
      type: product?.type || item.type || getSpecificationValue('Вид', 'Тип') || '—',
      sku: variant?.sku || item.sku || '—',
      price: product?.price != null ? formatMoney(product.price) : item.price || '—',
      comparePrice:
        product?.compare_at_price > product?.price
          ? formatMoney(product.compare_at_price)
          : item.comparePrice || '',
      available: typeof product?.available === 'boolean' ? product.available : item.available !== false,
      image: safeWishlistImageUrl(productImage, '') || safeWishlistImageUrl(item.image, ''),
      url: safeStoreUrl(product?.url || item.url),
      technicalSpecs,
    };
  };

  const alignTechnicalSpecifications = (items) => {
    const specificationLabels = [];
    const seenLabels = new Set();

    items.forEach((item) => {
      (item.technicalSpecs || []).forEach((specification) => {
        const normalizedLabel = specification.label.toLocaleLowerCase('bg-BG');
        if (seenLabels.has(normalizedLabel)) return;
        seenLabels.add(normalizedLabel);
        specificationLabels.push({ label: specification.label, normalizedLabel });
      });
    });

    return items.map((item) => {
      const valuesByLabel = new Map(
        (item.technicalSpecs || []).map((specification) => [
          specification.label.toLocaleLowerCase('bg-BG'),
          specification.value,
        ])
      );

      return {
        ...item,
        technicalSpecs: specificationLabels.map(({ label, normalizedLabel }) => ({
          label,
          value: valuesByLabel.get(normalizedLabel) || '—',
        })),
      };
    });
  };

  const renderCompareCard = (item) => {
    const title = escapeHtml(item.title);
    const imageMarkup = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${title}" loading="eager">`
      : '<span class="ts-wishlist-compare-card__image-placeholder" aria-hidden="true">♡</span>';
    const technicalSpecificationsMarkup = item.technicalSpecs?.length
      ? `
        <section class="ts-wishlist-compare-card__technical" aria-label="Технически спецификации">
          <h4>Технически спецификации</h4>
          <dl class="ts-wishlist-compare-card__specs ts-wishlist-compare-card__technical-specs">
            ${item.technicalSpecs.map((specification) => `
              <div>
                <dt>${escapeHtml(specification.label)}</dt>
                <dd>${escapeHtml(specification.value)}</dd>
              </div>
            `).join('')}
          </dl>
        </section>
      `
      : '';

    return `
      <article class="ts-wishlist-compare-card">
        <a class="ts-wishlist-compare-card__image" href="${escapeHtml(item.url)}">${imageMarkup}</a>
        <div class="ts-wishlist-compare-card__vendor">${escapeHtml(item.vendor)}</div>
        <a class="ts-wishlist-compare-card__title" href="${escapeHtml(item.url)}">${title}</a>
        <div class="ts-wishlist-compare-card__price">
          ${item.comparePrice ? `<s>${escapeHtml(item.comparePrice)}</s>` : ''}
          <strong>${escapeHtml(item.price)}</strong>
        </div>
        <dl class="ts-wishlist-compare-card__specs">
          <div><dt>Наличност</dt><dd class="${item.available ? 'is-available' : 'is-unavailable'}">${item.available ? 'В наличност' : 'Изчерпан'}</dd></div>
          <div><dt>Марка</dt><dd>${escapeHtml(item.vendor)}</dd></div>
          <div><dt>Тип</dt><dd>${escapeHtml(item.type)}</dd></div>
          <div><dt>Код</dt><dd>${escapeHtml(item.sku)}</dd></div>
        </dl>
        ${technicalSpecificationsMarkup}
        <a class="ts-wishlist-compare-card__link" href="${escapeHtml(item.url)}">Разгледай продукта <span aria-hidden="true">→</span></a>
      </article>
    `;
  };

  const openComparison = async () => {
    pruneCompareSelection();
    if (compareSelection.size < 2) return;

    const selectedItems = wishlist
      .filter((item) => compareSelection.has(String(item.id)))
      .slice(0, maxCompareItems);
    const listView = document.querySelector('[data-ts-wishlist-list-view]');
    const compareView = document.querySelector('[data-ts-wishlist-compare-view]');
    const compareGrid = document.querySelector('[data-ts-wishlist-compare-grid]');
    const panel = document.querySelector('[data-ts-wishlist-panel]');
    if (!compareView || !compareGrid) return;

    const requestId = compareRequestId + 1;
    compareRequestId = requestId;
    if (listView) listView.hidden = true;
    compareView.hidden = false;
    panel?.classList.add('is-comparing');
    compareGrid.innerHTML = selectedItems.map(() => '<div class="ts-wishlist-compare-loading" aria-hidden="true">' + (document.querySelector('[data-ts-loading-template]')?.innerHTML || '') + '</div>').join('');
    updateCompareControls();

    const details = alignTechnicalSpecifications(
      await Promise.all(selectedItems.map(getWishlistProductDetails))
    );
    if (requestId !== compareRequestId) return;

    compareGrid.innerHTML = details.map(renderCompareCard).join('');
    document.getElementById('ts-wishlist-compare-title')?.focus?.({ preventScroll: true });
  };

  const syncWishlist = () => {
    updateButtons();
    updateHeader();
    renderWishlist();
  };

  const closeDrawerBeforeWishlist = (trigger) => {
    const drawer = trigger?.closest?.('header-drawer');
    const details = drawer?.querySelector('#Details-menu-drawer-container');
    if (!drawer || !details?.hasAttribute('open')) return false;

    const summary = details.querySelector('summary');
    details.classList.remove('menu-opening');
    details.querySelectorAll('details').forEach((submenu) => {
      submenu.removeAttribute('open');
      submenu.classList.remove('menu-opening');
    });
    details.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    details.removeAttribute('open');
    summary?.setAttribute('aria-expanded', 'false');

    document.documentElement.classList.remove('ts-drawer-open');
    document.querySelector('.section-header')?.classList.remove('menu-open');
    document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-desktop', 'overflow-hidden-mobile');
    if (drawer.dataset.breakpoint) {
      document.body.classList.remove(`overflow-hidden-${drawer.dataset.breakpoint}`);
    }
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }
    if (typeof drawer.onResize === 'function') {
      window.removeEventListener('resize', drawer.onResize);
    }

    return true;
  };

  const openWishlist = (trigger = null) => {
    const panel = document.querySelector('[data-ts-wishlist-panel]');
    if (!panel) return;

    if (panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }

    closeDrawerBeforeWishlist(trigger);
    showWishlistList();
    renderWishlist();
    window.clearTimeout(closeTimer);
    window.cancelAnimationFrame(openFrame);
    window.clearTimeout(openTimer);
    panel.hidden = false;
    document.documentElement.classList.add('ts-wishlist-open');
    document.querySelectorAll('[data-ts-wishlist-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
    });

    const showPanel = () => {
      if (panel.hidden || !document.documentElement.classList.contains('ts-wishlist-open')) return;

      const shouldFocus = !panel.classList.contains('is-open');
      panel.classList.add('is-open');
      if (shouldFocus) {
        panel.querySelector('.ts-wishlist-panel__dialog')?.focus({ preventScroll: true });
      }
    };

    openFrame = window.requestAnimationFrame(showPanel);
    openTimer = window.setTimeout(showPanel, 40);
  };

  const closeWishlist = () => {
    const panel = document.querySelector('[data-ts-wishlist-panel]');
    if (!panel) return;

    window.cancelAnimationFrame(openFrame);
    window.clearTimeout(openTimer);
    openFrame = null;
    openTimer = null;
    panel.classList.remove('is-open');
    document.documentElement.classList.remove('ts-wishlist-open');
    document.querySelectorAll('[data-ts-wishlist-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      if (!panel.classList.contains('is-open')) {
        panel.hidden = true;
        showWishlistList();
      }
    }, 540);
  };

  const scheduleSync = () => {
    if (refreshFrame) return;
    refreshFrame = window.requestAnimationFrame(() => {
      refreshFrame = null;
      syncWishlist();
    });
  };

  readWishlist();

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-ts-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();

      const item = getItemFromButton(toggle);
      if (!item) return;

      const added = !isFavorite(item.id);
      if (!added) {
        wishlist = wishlist.filter((favorite) => favorite.id !== item.id);
      } else {
        wishlist = [item, ...wishlist.filter((favorite) => favorite.id !== item.id)];
      }

      writeWishlist();
      syncWishlist();
      playWishlistAnimation(toggle, added);
      showWishlistToast(item, added);
      return;
    }

    const remove = event.target.closest('[data-ts-wishlist-remove]');
    if (remove) {
      event.preventDefault();
      const removedItem = wishlist.find((favorite) => favorite.id === remove.dataset.tsWishlistRemove);
      wishlist = wishlist.filter((favorite) => favorite.id !== remove.dataset.tsWishlistRemove);
      writeWishlist();
      syncWishlist();
      showWishlistToast(removedItem, false);
      return;
    }

    const compareToggle = event.target.closest('[data-ts-wishlist-compare-toggle]');
    if (compareToggle) {
      event.preventDefault();
      const id = String(compareToggle.dataset.tsWishlistCompareToggle || '');
      if (!id) return;

      if (compareSelection.has(id)) {
        compareSelection.delete(id);
      } else if (compareSelection.size >= maxCompareItems) {
        updateCompareControls('Може да сравниш до 4 продукта');
        window.setTimeout(() => updateCompareControls(), 1800);
        return;
      } else {
        compareSelection.add(id);
      }

      updateCompareControls();
      return;
    }

    if (event.target.closest('[data-ts-wishlist-compare]')) {
      event.preventDefault();
      openComparison();
      return;
    }

    if (event.target.closest('[data-ts-wishlist-compare-back]')) {
      event.preventDefault();
      showWishlistList();
      return;
    }

    const wishlistTrigger = event.target.closest('[data-ts-wishlist-trigger]');
    if (wishlistTrigger) {
      event.preventDefault();
      openWishlist(wishlistTrigger);
      return;
    }

    if (event.target.closest('[data-ts-wishlist-close]')) {
      event.preventDefault();
      closeWishlist();
    }
  });

  document.addEventListener('keydown', (event) => {
    const panel = document.querySelector('[data-ts-wishlist-panel]');
    if (event.key === 'Escape' && panel && !panel.hidden) {
      closeWishlist();
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key === storageKey) {
      readWishlist();
      syncWishlist();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    readWishlist();
    syncWishlist();
  });

  document.addEventListener('shopify:section:load', () => {
    readWishlist();
    syncWishlist();
  });

  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      if (mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => node.nodeType === 1 && (node.matches?.('[data-ts-wishlist-toggle]') || node.querySelector?.('[data-ts-wishlist-toggle]'))))) {
        scheduleSync();
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  syncWishlist();
})();

(() => {
  const cartSelector = '#cart-icon-bubble';
  let refreshFrame = null;

  const getCartCount = (bubble) => {
    if (!bubble) return 0;

    const countNode =
      bubble.querySelector('[data-ts-cart-count]') ||
      bubble.querySelector('span[aria-hidden="true"]') ||
      bubble.querySelector('span:not(.visually-hidden)');
    const count = Number.parseInt((countNode?.textContent || '').replace(/[^\d]/g, ''), 10);

    return Number.isFinite(count) ? count : 0;
  };

  const normalizeCartIcon = () => {
    const cartIcon = document.querySelector(cartSelector);
    if (!cartIcon) return;

    const nestedCartIcon = cartIcon.querySelector(cartSelector);
    if (nestedCartIcon) {
      cartIcon.innerHTML = nestedCartIcon.innerHTML;
    }

    cartIcon.classList.add('ts-header-action--cart');
    cartIcon.setAttribute('data-ts-cart-icon-bubble', '');

    if (!cartIcon.querySelector('[data-ts-cart-icon-graphic]')) {
      const template = document.querySelector('[data-ts-cart-icon-template]');
      const currentGraphic =
        cartIcon.querySelector('.svg-wrapper') ||
        cartIcon.querySelector('svg.icon-cart') ||
        cartIcon.querySelector('svg.icon-cart-empty') ||
        cartIcon.querySelector('.icon-cart') ||
        cartIcon.querySelector('.icon-cart-empty');

      if (template?.content?.firstElementChild) {
        const replacement = template.content.firstElementChild.cloneNode(true);

        if (currentGraphic) {
          currentGraphic.replaceWith(replacement);
        } else {
          cartIcon.prepend(replacement);
        }
      }
    }

    const existingBubbles = Array.from(cartIcon.querySelectorAll('.ts-cart-count-bubble, .cart-count-bubble'));
    let bubble = existingBubbles.find((item) => item.querySelector('[data-ts-cart-count]')) || existingBubbles[0];
    existingBubbles.forEach((item) => {
      if (item !== bubble) item.remove();
    });

    if (!bubble) {
      bubble = document.createElement('span');
      bubble.className = 'cart-count-bubble ts-cart-count-bubble';
      bubble.setAttribute('data-ts-cart-count-bubble', '');
      bubble.hidden = true;
      cartIcon.appendChild(bubble);
    }

    bubble.classList.add('cart-count-bubble', 'ts-cart-count-bubble');
    bubble.setAttribute('data-ts-cart-count-bubble', '');

    let countNode =
      bubble.querySelector('[data-ts-cart-count]') ||
      bubble.querySelector('span[aria-hidden="true"]') ||
      bubble.querySelector('span:not(.visually-hidden)');

    if (!countNode) {
      countNode = document.createElement('span');
      countNode.setAttribute('aria-hidden', 'true');
      bubble.prepend(countNode);
    }

    countNode.setAttribute('aria-hidden', 'true');
    countNode.setAttribute('data-ts-cart-count', '');

    const count = getCartCount(bubble);
    countNode.textContent = count > 99 ? '99+' : String(count);
    bubble.hidden = count <= 0;
    cartIcon.classList.toggle('ts-cart-has-items', count > 0);
  };

  const scheduleNormalize = () => {
    if (refreshFrame) return;

    refreshFrame = window.requestAnimationFrame(() => {
      refreshFrame = null;
      normalizeCartIcon();
    });
  };

  document.addEventListener('DOMContentLoaded', scheduleNormalize);
  document.addEventListener('shopify:section:load', scheduleNormalize);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleNormalize);
  }

  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      const shouldNormalize = mutations.some((mutation) => {
        if (mutation.target?.id === 'cart-icon-bubble') return true;

        return Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType !== 1) return false;

          return (
            node.matches?.(cartSelector) ||
            node.querySelector?.(cartSelector) ||
            node.matches?.('.cart-count-bubble') ||
            node.querySelector?.('.cart-count-bubble')
          );
        });
      });

      if (shouldNormalize) scheduleNormalize();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  scheduleNormalize();
})();
